<script>
import {TableHelper} from "../../helpers/TableHelper";
import {Listeners} from "../../constants/listeners";
import ContextMenuMixin from "./mixins/ContextMenuMixin";

/**
 * @method setDefaultParams() - назначение дефолтных значений параметров для ситуаций, когда нет возможности назначить в data()
 * @method update(event) - для событий таблицы: @page - переключение страниц, @sort - переключение сортировки
 * @method getDataForTableActions() - назначение данных для модалки "action" таблиц, срабатывает каждый раз при выборе чекбокса
 *
 * Mixin контекстного меню.
 * Для компонента контекстного меню в шаблоне необходима ref-ссылка: ref="contextMenu", у елемента @contextmenu c методом "getContextMenu".
 * Для многих элементов должны совпадать наименование параметра полученных данных и значение параметра "field" у колонки.
 * Доступны следующие IDTypes пунктов меню: matches,
 * @method getContextMenu(event, rowData, cellName, [IDTypes]) - динамически формирует пункты контекстное меню и открывает
 * @computed getContextMenuItems() - проставляется в :model компонента контекстного меню, которое возвращает динамически
 *  сформированные пункты меню
 */
export default {
    name: "BaseReport",
    emits: [
        // при нажатии применения фильтра (KS-Filter), считывает хеш и загружает данные
        Listeners.LOAD_GROUP_REPORT_DATA_FROM_HASH,
        // очистка выбранных строк
        Listeners.TABLE_CLEAR_SELECTED,
    ],
    mixins: [ContextMenuMixin],
    data() {
        return {
            loading: false,
            error: null,
            info: null,
            tableData: null,
            params: [],
            selected: null,
            contextMenuItems: [],
        };
    },
    beforeMount() {
        this.$emitter.on(Listeners.RESET_FILTER_GROUP_REPORT, () => this.resetFilter());
        this.$emitter.on(Listeners.LOAD_GROUP_REPORT_DATA_FROM_HASH, () => this.load());
        this.$emitter.on(Listeners.TABLE_CLEAR_SELECTED, this.clearSelected);

        this.setDefaultParams();
        this.load();
    },
    methods: {
        /** публичные методы */
        setDefaultParams() {},
        getDataForTableActions() {},
        update(event) {
            this.clearSelected();
            this.params['sort'] = event.multiSortMeta;
            this.params.per_page = event.rows;
            this.params.page = event.page ? event.page + 1 : 1;

            let encodedParams = this.getEncodedParams();
            this.updateHash(encodedParams);
            this.loadData(encodedParams);
        },
        /** приватные методы */
        async load() {
            this.clearSelected();
            this.setParams();

            let encodedParams = this.getEncodedParams();
            // await из-за сбрасывания хеша, когда отсутствует при первой загрузке
            await this.loadData(encodedParams);
            this.updateHash(encodedParams);
        },
        // парсинг hash - параметров и объединение с дефолтными занчениями
        setParams() {
            let hashes = new URL(decodeURIComponent(window.location)).hash.replace(/^#+/, '').split('&');
            if (!hashes) {
                return;
            }

            let params = {};
            hashes.forEach(item => {
                if (item.startsWith('filter=')) {
                    // у фильтров неудобные символы, такие как "="
                    params['filter'] = decodeURIComponent(item.replace('filter=', ''));
                    return;
                }

                const split = item.split('=');
                if (split.length < 2 || !split[1]) {
                    // нет значения параметра
                    return;
                }

                params[split[0]] = split[1];
            });

            if (params['sort']) {
                params['sort'] = this.decodeUrlSorts(params['sort']);
            }

            this.params = Object.assign(this.params, params);
        },
        // очищаем фильтр
        resetFilter() {
            this.params['filter'] = '';
            let encodedParams = this.getEncodedParams();
            this.updateHash(encodedParams);
            this.load();
        },
        // обновляем хеш
        updateHash(params) {
            let hashArray = [];
            Object.entries(params).forEach(item => {
                if (!item[1]) {
                    // нет значения параметра
                    return;
                }

                hashArray.push(item.join('='));
            });

            window.location.hash = hashArray.join('&');
        },
        // получаем преобразованные параметры в виде {key: value} с сортировкой в виде строки
        getEncodedParams() {
            let params = Object.assign({}, this.params);
            if (params['sort']) {
                params['sort'] = this.encodeUrlSorts(params['sort'])
            }
            if (params['filter']) {
                params['filter'] = encodeURI(params['filter']);
            }

            return params;
        },
        // декодер параметров сортировки для DataTable
        decodeUrlSorts(sortParams) {
            let sorts = [];
            sortParams.split(',').forEach(sortParam => {
                const item = sortParam.split('|');
                if (!item[1]) {
                    return;
                }

                sorts.push({
                    field: item[0],
                    order: TableHelper.getDecodeSortOrder(item[1])
                });
            })

            return sorts;
        },
        // преобразование сортировки DataTable в строку для hash и API параметра запроса
        encodeUrlSorts(sortParams) {
            let sorts = [];
            if (!sortParams) {
                return;
            }

            Object.values(sortParams).forEach(item => {
                sorts.push(item.field + '|' + TableHelper.getEncodeSortOrder(item.order));
            });

            return sorts.join();
        },
        // очистка выбранных строк
        clearSelected() {
            this.selected = null;
        }
    },
    computed: {
        getRowsPerPageOptions() {
            return TableHelper.getRowsPerPageOptions();
        },
        getPaginatorTemplate() {
            return TableHelper.getDefaultPaginatorTemplate();
        },
    },
    watch: {
        // отслеживание выбранных чекбоксов
        selected() {
            this.$emitter.emit(Listeners.TABLE_CHECKBOX_SELECTED, this.getDataForTableActions());
        }
    },
}
</script>
